"use client";

import { useEffect, useState } from "react";
import type { PublicCatalogItem } from "@bash/shared";
import { listPublicCatalog } from "@/lib/api/catalog";
import Masthead from "@/components/atoms/masthead";
import { PublicCard } from "./PublicCard";

const PAGE_LIMIT = 48;

interface PublicCatalogViewProps {
  type: string;
  title: string;
  subtitle: string;
}

export function PublicCatalogView({
  type,
  title,
  subtitle,
}: PublicCatalogViewProps) {
  const [items, setItems] = useState<PublicCatalogItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listPublicCatalog(type, { limit: PAGE_LIMIT, offset: 0 })
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setHasMore(page.has_more);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load catalog. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type]);

  async function loadMore() {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listPublicCatalog(type, {
        limit: PAGE_LIMIT,
        offset: items.length,
      });
      setItems((prev) => [...prev, ...page.items]);
      setHasMore(page.has_more);
    } catch {
      // silently ignore load-more errors; user can retry
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <>
      <Masthead title={title} subtitle={subtitle} />

      {error ? (
        <p className="text-3.5 text-fg-muted">{error}</p>
      ) : loading ? (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(24rem,1fr))]">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-52 animate-pulse-subtle-1 rounded-20 border border-border-subtle bg-bg-muted"
            />
          ))}
        </div>
      ) : items.length > 0 ? (
        <>
          <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(24rem,1fr))]">
            {items.map((item) => (
              <PublicCard key={item.slug} item={item} type={type} />
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="flex justify-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex h-9 items-center rounded-full border border-border-subtle bg-bg-elevated px-5 text-3.5 font-medium text-fg-default transition hover:border-border-muted disabled:opacity-50"
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-3.5 text-fg-muted">Nothing here yet.</p>
      )}
    </>
  );
}
